import React, {
  useState,
  useEffect
} from 'react';

import { makeStyles } from '@material-ui/core/styles';
import {
  Paper,
  Avatar,
  Button,
  Typography,
  LinearProgress,
  Collapse,
  Grid,
  FormControl,
  FormHelperText,
  TextField,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Divider,
  ButtonBase,
} from '@material-ui/core';
import {
  Skeleton
} from '@material-ui/lab';

import { grey } from '@material-ui/core/colors';
import Alert from '@material-ui/lab/Alert';
import ImageIcon from '@material-ui/icons/Image';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';

import Dropzone from 'react-dropzone'

import localization from '../../../utils/localizations';
import bgImage from '../../../img/profile-background-small.jpg';
import {
  useProfile
} from '../../../data/Profile';


const useStyles = makeStyles((theme) => ({
  mainBody: {
    position: 'relative'
  },
  profileBackground: {
    width: '100%'
  },
  profileAvatarContainer: {
    position: 'relative',
    height: 150,
    overflow: 'hidden',
  },
  avatarCenteringContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
  },
  avatar: {
    width: 90,
    height: 90,
    color: theme.palette.getContrastText(grey[300]),
    backgroundColor: grey[300],
  },
  avatarButtonContainer: {
    borderRadius: 100,
  },
  userInfoContainer: {
    padding: 10,
  },
  marginBottom: {
    marginBottom: 16,
  },
  userFullName: {
    fontSize: '1.4rem',
    textAlign: 'center',
  },
  userNim: {
    letterSpacing: '.1rem',
    color: 'grey',
    textAlign: 'center',
  },
  userFaculty: {
    fontSize: '.75rem', 
    textAlign: 'center',
  },
  buttons: {
    display: "flex",
    justifyContent: "space-around",
  },
  form: {
    marginTop: 20,
  },
  uploadContainer: {
    padding: 10,
    marginBottom: 20,
  },
  dropzone: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 200,
    padding: 20,
    borderWidth: 2,
    borderRadius: 2,
    borderColor: '#eeeeee',
    borderStyle: 'dashed',
    backgroundColor: '#fafafa',
    color: '#bdbdbd',
    outline: 'none',
    transition: 'border .24s ease-in-out',
    cursor: 'pointer',
    marginBottom: 10,
  },
  textAlignCenter: {
    textAlign: 'center',
  },
  textAlignRight: {
    textAlign: 'right',
  },
}));
 

function UserProfile() {
  const classes = useStyles();
  const [profile, profileLoading, updateProfile, updateProfilePicture] = useProfile();
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [fullName, setFullName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [subProgram, setSubProgram] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [errors, setErrors] = useState({} as any);
  const [showAvatarForm, setShowAvatarForm] = useState(false);
  const [selectedAvatarUrl, setSelectedAvatarUrl] = useState('');
  const [selectedFile, setSelectedFile] = useState({} as any);

  const onShowEditForm = (e: any) => {
    e.preventDefault();
    setFullName(profile.full_name);
    setPhoneNumber(profile.phone_number);
    setSubProgram(profile.sub_program);
    setShowEditProfile(true);
  }

  useEffect(() => {
    setSelectedAvatarUrl(profile.avatar);
  }, [profile]);

  const onSubmitForm = async (e: any) => {
    e.preventDefault();
    if (profileLoading) return;

    let data: any = {
      full_name: fullName,
      sub_program: subProgram,
    };

    if (phoneNumber) data.phone_number = phoneNumber;
    if (currentPassword) data.current_password = currentPassword;
    if (password) data.password = password;
    if (password2) data.password_2 = password2;

    const [success, result] = await updateProfile(data);

    if (success) {
      setShowEditProfile(false);
      setErrors({})
      setCurrentPassword('')
      setPassword('')
      setPassword2('')
    }
    else if (result && result.errors) {
      setErrors(result.errors);
    }
  }

  const hideAndClearAvatarForm = async () => {
    setShowAvatarForm(false);
    setSelectedFile({});
    setShowAvatarForm(false);
  }

  const updateAvatar = async () => {
    if (profileLoading) return;
    if (!selectedFile.name) return;

    const [success, result] = await updateProfilePicture(selectedFile);

    if (success) {
      hideAndClearAvatarForm();
    }
    else if (result && result.errors) {
      setErrors(result.errors);
    }
  }

  const getErrorMessage = (messages: string[]) => {
    if (!messages) return '';

    let message = '';
    messages.forEach((m: string) => {
      message = `${message} ${m}`;
    })

    return message;
  }

  return (
  <Paper className={classes.mainBody} square>
    <div className={classes.profileAvatarContainer}>
      <div className={classes.avatarCenteringContainer}>
        <ButtonBase className={classes.avatarButtonContainer} onClick={() => setShowAvatarForm(!showAvatarForm)}>
          <Avatar className={classes.avatar} src={selectedAvatarUrl} />
        </ButtonBase>
      </div>
      <img src={bgImage} alt="" className={classes.profileBackground} />
    </div>


    <Collapse
      in={showAvatarForm}
    >
      <Dropzone
        accept=".jpeg,.jpg,.png"
        onDrop={(acceptedFiles: any[]) => {
          if (acceptedFiles.length > 0) {
            const file = acceptedFiles[0];

            const reader = new FileReader()
            reader.onload = () => {
              const base64Url = reader.result;
              if (base64Url) {
                setSelectedAvatarUrl(base64Url as string);
              }
            };

            reader.readAsDataURL(file)
            setSelectedFile(file);
          }
        }}
      >
        {({getRootProps, getInputProps}) => (
          <section className={classes.uploadContainer}>
            <div className={classes.dropzone} {...getRootProps()}>
              <input {...getInputProps()} />
              <CloudUploadIcon fontSize="large" />
              <Typography className={classes.textAlignCenter}>Choose a file or drag here</Typography>
            </div>
            <div>
            {selectedFile.name &&
              <div className={classes.textAlignRight}>
                <Chip
                  avatar={<Avatar alt="" src={selectedAvatarUrl} />}
                  label={selectedFile.name ? selectedFile.name : ''}
                  className={classes.marginBottom}
                  onDelete={() => {
                    setSelectedFile({});
                    setSelectedAvatarUrl(profile.avatar);
                  }}
                />
              </div>
            }
            </div>
            <div className={classes.buttons}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                disabled={profileLoading}
                onClick={(e) => {
                  e.preventDefault();
                  updateAvatar();
                }}
              >{localization.save}</Button>
              <Button
                size="small"
                onClick={(e) => {
                  e.preventDefault();
                  hideAndClearAvatarForm();
                  setSelectedAvatarUrl(profile.avatar);
                }}
              >{localization.cancel}</Button>
            </div>
          </section>
        )}
      </Dropzone>
    </Collapse>


    <Collapse
      in={!showEditProfile}
      {...(!showEditProfile ? { timeout: 300 } : {})}
    >
      <div className={classes.userInfoContainer}>
        <div className={classes.marginBottom}>
          {profileLoading ? (
            <Skeleton variant="rect" width={'100%'} height={20} />
          ) : (
            <Typography className={classes.userFullName}>{profile.full_name}</Typography>
          )}
          {profileLoading ? (
            <Skeleton variant="rect" width={'100%'} height={20} />
          ) : (
            <Typography className={classes.userNim}>{profile.nim}</Typography>
          )}

          <Typography className={classes.userFaculty}>{profile.sub_program_name}</Typography>
        </div>

        <div className={classes.marginBottom}>
          <Typography className={classes.userFaculty}>{localization.thesisProgress}: {profile.thesis_total_steps ? `${profile.thesis_progress} / ${profile.thesis_total_steps}` : '0%'}</Typography>
          <LinearProgress variant="determinate" value={profile.thesis_progress ? (profile.thesis_progress / profile.thesis_total_steps) * 100 : 0} />
        </div>

        <div className={classes.buttons}>
          <Button
            size="small"
            onClick={onShowEditForm}
          >{localization.editProfile}</Button>
        </div>
      </div>
    </Collapse>


    <Collapse
      in={showEditProfile}
      {...(showEditProfile ? { timeout: 300 } : {})}
    >
      <div className={classes.userInfoContainer}>
        <form onSubmit={onSubmitForm} className={classes.form}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={12}>
              <FormControl fullWidth>
                <TextField
                  id="full_name"
                  label={localization.fullName}
                  variant="outlined"
                  value={fullName}
                  onChange={(event: any) => setFullName(event.target.value)}
                  disabled={profileLoading}
                  error={Boolean(errors.full_name)}
                  helperText={getErrorMessage(errors.full_name)}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={12}>
              <FormControl fullWidth>
                <TextField
                  id="phone_number"
                  label={localization.phoneNumber}
                  variant="outlined"
                  value={phoneNumber}
                  onChange={(event: any) => setPhoneNumber(event.target.value)}
                  disabled={profileLoading}
                  error={Boolean(errors.full_name)}
                  helperText={getErrorMessage(errors.phone_number)}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={12}>
              <FormControl variant="outlined" fullWidth error={Boolean(errors.sub_program)}>
                <InputLabel id="sub_program_label">{localization.subProgram}</InputLabel>
                <Select
                  labelId="sub_program"
                  id="sub_program"
                  value={subProgram}
                  onChange={(event: any) => setSubProgram(event.target.value)}
                  label={localization.subProgram}
                  disabled={profileLoading}
                >
                  {profile.available_sub_programs ? profile.available_sub_programs.map((item: any, i: number) => 
                    <MenuItem value={item.id} key={`sub_program_menu_item_${item.id}`}>{item.name}</MenuItem>
                  ) : undefined}
                </Select>

                {Boolean(errors.sub_program) &&
                  <FormHelperText>{getErrorMessage(errors.sub_program)}</FormHelperText>
                }
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={12}>
              <Divider />
              </Grid>

            <Grid item xs={12} sm={12}>
              <Typography>{localization.changePassword}</Typography>
            </Grid>

            <Grid item xs={12} sm={12}>
              <FormControl fullWidth>
                <TextField
                  id="current_password"
                  label={localization.currentPassword}
                  variant="outlined"
                  value={currentPassword}
                  onChange={(event: any) => setCurrentPassword(event.target.value)}
                  disabled={profileLoading}
                  type="password"
                  autoComplete="current-password"
                  error={Boolean(errors.current_password)}
                  helperText={getErrorMessage(errors.current_password)}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={12}>
              <FormControl fullWidth>
                <TextField
                  id="password"
                  label={localization.newPassword}
                  variant="outlined"
                  value={password}
                  onChange={(event: any) => setPassword(event.target.value)}
                  disabled={profileLoading}
                  type="password"
                  error={Boolean(errors.password)}
                  helperText={getErrorMessage(errors.password)}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={12}>
              <FormControl fullWidth>
                <TextField
                  id="password_2"
                  label={localization.newPasswordRepeat}
                  variant="outlined"
                  value={password2}
                  onChange={(event: any) => setPassword2(event.target.value)}
                  disabled={profileLoading}
                  type="password"
                  error={Boolean(errors.password_2)}
                  helperText={getErrorMessage(errors.password_2)}
                />
              </FormControl>
            </Grid>

            {Boolean(errors.non_field_errors) &&
              <Grid item xs={12} sm={12}>
                <Alert severity="error">{getErrorMessage(errors.non_field_errors)}</Alert>
              </Grid>
            }

            <Grid item xs={12} sm={12}>
              <div className={classes.buttons}>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  type="submit"
                  disabled={profileLoading}
                >{localization.save}</Button>
                <Button
                  size="small"
                  onClick={(e) => {
                    e.preventDefault()
                    setShowEditProfile(false);
                  }}
                >{localization.cancel}</Button>
              </div>
            </Grid>
          </Grid>
        </form>
      </div>
    </Collapse>

  </Paper>
  );
}

export {UserProfile};